/**
 * F7.6 (#13) — Gestión de usuarios (staff). Admin-only.
 *
 * Lista usuarios, cambia rol (Select inline), activa/desactiva (baja lógica) y resetea MFA.
 * El alta de cuentas nuevas sigue vía Firebase Console (no se crea desde acá).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui/table'
import { useToast } from '@/components/ui/use-toast'

interface AdminUser {
  id: string
  email: string
  role: string
  active: boolean
  last_login_at: string | null
  created_at: string
}

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'medico', label: 'Médico' },
  { value: 'tens', label: 'TENS' },
  { value: 'digitador', label: 'Digitador' },
  { value: 'recepcionista', label: 'Recepcionista' },
]

export default function SettingsUsersPage() {
  const qc = useQueryClient()
  const { show } = useToast()

  const list = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.get<{ users: AdminUser[] }>('/api/admin/users'),
  })

  const patchUser = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { role?: string; active?: boolean } }) =>
      api.patch(`/api/admin/users/${encodeURIComponent(id)}`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-users'] }),
    onError: (e) =>
      show({
        kind: 'error',
        title: 'No se pudo actualizar',
        description: e instanceof Error ? e.message : 'Error desconocido',
      }),
  })

  const resetMfa = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/users/${encodeURIComponent(id)}/mfa/reset`),
    onSuccess: () =>
      show({
        kind: 'success',
        title: 'MFA reseteado',
        description: 'El usuario re-enrolará MFA en su próximo login.',
      }),
  })

  const users = list.data?.users ?? []

  return (
    <div className="flex flex-col gap-4 p-4" data-testid="settings-users-page">
      <header>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">
          Configuración — Usuarios
        </h1>
        <p className="text-sm text-slate-600">
          Gestión de roles, alta/baja lógica y reset de MFA (solo administradores). El alta de
          cuentas nuevas se aprovisiona vía Firebase Console.
        </p>
      </header>

      <TableContainer>
        <Table data-testid="settings-users-table">
          <TableHead>
            <TableRow>
              <TableHeaderCell>Email</TableHeaderCell>
              <TableHeaderCell>Rol</TableHeaderCell>
              <TableHeaderCell>Estado</TableHeaderCell>
              <TableHeaderCell>Último login</TableHeaderCell>
              <TableHeaderCell>MFA</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.isPending && (
              <TableRow>
                <TableCell colSpan={5} className="py-6 text-center text-slate-500">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </TableCell>
              </TableRow>
            )}
            {!list.isPending && users.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-6 text-center text-slate-500">
                  Sin usuarios.
                </TableCell>
              </TableRow>
            )}
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-medium text-slate-800">{u.email}</TableCell>
                <TableCell>
                  <Select
                    value={u.role}
                    onChange={(role) => patchUser.mutate({ id: u.id, body: { role } })}
                    options={ROLE_OPTIONS}
                    className="min-w-[140px]"
                    data-testid={`user-role-${u.id}`}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => patchUser.mutate({ id: u.id, body: { active: !u.active } })}
                    data-testid={`user-active-${u.id}`}
                  >
                    {u.active ? 'Activo' : 'Inactivo'}
                  </Button>
                </TableCell>
                <TableCell className="text-xs text-slate-500">
                  {u.last_login_at
                    ? new Date(u.last_login_at).toLocaleString('es-CL', {
                        timeZone: 'America/Santiago',
                      })
                    : '—'}
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => resetMfa.mutate(u.id)}
                    data-testid={`user-mfa-${u.id}`}
                  >
                    Reset MFA
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}
